import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { showToast } from '../config_and_helpers/helpers';
import { logError, logInfo } from '../config_and_helpers/logger';
import { isCanceledRequest } from '../utils/listSearchRequest';

const LOG_CONTEXT = 'protocol_client';

/**
 * Normalize a DRF error payload into user-facing message strings.
 *
 * @param {*} data - `error.response.data`
 * @returns {Array<string>}
 */
function formatProtocolErrorMessages(data) {
  if (!data) return [];
  if (typeof data === 'string') return [data];
  if (Array.isArray(data)) return data.filter(Boolean).map(String);
  if (typeof data === 'object') {
    return Object.values(data)
      .flat()
      .filter(Boolean)
      .map(String);
  }
  return [];
}

/**
 * Map a failed protocol request to a structured result the UI can act on.
 *
 * Cross-lab and non-existent ids both return 404 by design (ids must not be
 * enumerable), so both are surfaced as "not found in your lab".
 *
 * @param {*} error - Axios error
 * @returns {{ success: false, status: number|undefined, fieldErrors: Object|null, messages: Array<string> }}
 */
function resolveProtocolError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 403) {
    return {
      success: false,
      status,
      fieldErrors: null,
      messages: ['Protocols are not available for supplier accounts.'],
    };
  }

  if (status === 404) {
    return {
      success: false,
      status,
      fieldErrors: null,
      messages: ['Not found in your lab.'],
    };
  }

  const messages = formatProtocolErrorMessages(data);

  return {
    success: false,
    status,
    fieldErrors:
      status === 400 && data && typeof data === 'object' && !Array.isArray(data)
        ? data
        : null,
    messages: messages.length
      ? messages
      : [error?.message ?? 'An unexpected error occurred.'],
  };
}

/**
 * Fetch a page of protocols for the authenticated user's lab.
 *
 * The endpoint is always ordered by `name`, so no sort params are sent. The
 * first page is built here; later pages reuse the backend `next` URL verbatim
 * to avoid query drift between pages.
 *
 * @async
 * @param {string} token - Access token for the current user.
 * @param {Function} setProtocols - State updater; replaces on first page, appends on `nextPage`.
 * @param {Object} [options]
 * @param {string} [options.searchInput] - Matches name, description, and linked product cat_num/name.
 * @param {string|number} [options.productId] - Only protocols linked to that product pk.
 * @param {string} [options.catNum] - Only protocols linked to a product with that exact catalogue number.
 * @param {string|null} [options.nextPage] - Backend-provided next page URL.
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Object>} `{ success, nextPage?, reachedEnd? }`, `{ canceled: true }` on abort,
 * or the structured failure from `resolveProtocolError`.
 */
export const getProtocols = async (token, setProtocols, options = {}) => {
  const {
    searchInput = '',
    productId = '',
    catNum = '',
    nextPage = null,
    signal = undefined,
  } = options;

  let url = nextPage ? nextPage : `${BACKEND_URL}protocols/?page_num=1`;

  if (!nextPage) {
    if (searchInput) {
      url += `&search=${encodeURIComponent(searchInput)}`;
    }
    if (productId) {
      url += `&product_id=${encodeURIComponent(productId)}`;
    }
    if (catNum) {
      url += `&cat_num=${encodeURIComponent(catNum)}`;
    }
  }

  try {
    logInfo(LOG_CONTEXT, 'getProtocols start', {
      searchInput,
      productId,
      catNum,
      isNextPage: Boolean(nextPage),
    });

    const response = await axios.get(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
      signal,
    });

    const results = response.data?.results ?? [];
    const nextCursor = response.data?.next ?? null;

    if (setProtocols) {
      if (nextPage) {
        setProtocols(prevProtocols => [...prevProtocols, ...results]);
      } else {
        setProtocols(results);
      }
    }

    logInfo(LOG_CONTEXT, 'getProtocols success', {
      count: results.length,
      reachedEnd: !nextCursor,
    });

    if (!nextCursor) {
      return { success: true, reachedEnd: true, results };
    }

    return { success: true, nextPage: nextCursor, results };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    logError(LOG_CONTEXT, 'getProtocols failed', error.response?.data ?? error);
    return resolveProtocolError(error);
  }
};

/**
 * Fetch the full payload for a single protocol (includes `products[]`).
 *
 * @async
 * @param {string} token - Access token for the current user.
 * @param {string|number} protocolId
 * @returns {Promise<Object>} `{ success: true, data }` or the structured failure.
 */
export const getProtocolDetails = async (token, protocolId) => {
  try {
    logInfo(LOG_CONTEXT, 'getProtocolDetails start', { protocolId });

    const response = await axios.get(
      `${BACKEND_URL}protocols/${protocolId}/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    logInfo(LOG_CONTEXT, 'getProtocolDetails success', {
      protocolId,
      productCount: response.data?.product_count,
    });

    return { success: true, data: response.data };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      'getProtocolDetails failed',
      error.response?.data ?? error
    );
    return resolveProtocolError(error);
  }
};

/**
 * Create a protocol.
 *
 * `lab` is never sent — the backend resolves it from the authenticated user.
 * Omit `product_ids` to create the protocol without links.
 *
 * @async
 * @param {string} token - Access token for the current user.
 * @param {Object} protocolData - `{ name, description, url, product_ids? }`
 * @returns {Promise<Object>} `{ success: true, protocol, toast }` or the structured failure
 * (`fieldErrors.name` carries the duplicate-name 400).
 */
export const createProtocol = async (token, protocolData) => {
  try {
    logInfo(LOG_CONTEXT, 'createProtocol start', {
      name: protocolData?.name,
      productIdCount: protocolData?.product_ids?.length ?? null,
    });

    const response = await axios.post(
      `${BACKEND_URL}protocols/`,
      protocolData,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    logInfo(LOG_CONTEXT, 'createProtocol success', {
      protocolId: response.data?.id,
    });

    return {
      success: true,
      protocol: response.data,
      toast: () =>
        showToast('Protocol created successfully!', 'success', 'top-right', 3000),
    };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      'createProtocol failed',
      error.response?.data ?? error
    );
    return resolveProtocolError(error);
  }
};

/**
 * Update a protocol.
 *
 * Sending `product_ids` **replaces** the entire link set; omit the key to
 * leave links untouched.
 *
 * @async
 * @param {string} token - Access token for the current user.
 * @param {string|number} protocolId
 * @param {Object} updatedProtocolData - `{ name, description, url, product_ids? }`
 * @returns {Promise<Object>} `{ success: true, updatedProtocol, toast }` or the structured failure.
 */
export const updateProtocol = async (
  token,
  protocolId,
  updatedProtocolData
) => {
  try {
    logInfo(LOG_CONTEXT, 'updateProtocol start', {
      protocolId,
      name: updatedProtocolData?.name,
      replacesLinks: Object.prototype.hasOwnProperty.call(
        updatedProtocolData ?? {},
        'product_ids'
      ),
    });

    const response = await axios.patch(
      `${BACKEND_URL}protocols/${protocolId}/`,
      updatedProtocolData,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    logInfo(LOG_CONTEXT, 'updateProtocol success', { protocolId });

    return {
      success: true,
      updatedProtocol: response.data,
      toast: () =>
        showToast('Protocol updated successfully!', 'success', 'top-right', 3000),
    };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      'updateProtocol failed',
      error.response?.data ?? error
    );
    return resolveProtocolError(error);
  }
};

/**
 * Delete a protocol. Linked products are kept — only the join rows go.
 *
 * Returns the `{ success, toast }` shape `DeleteButton` expects.
 *
 * @async
 * @param {string} token - Access token for the current user.
 * @param {string|number} protocolId
 * @returns {Promise<Object>} `{ success: true, toast }` or the structured failure.
 */
export const deleteProtocol = async (token, protocolId) => {
  try {
    logInfo(LOG_CONTEXT, 'deleteProtocol start', { protocolId });

    await axios.delete(`${BACKEND_URL}protocols/${protocolId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    logInfo(LOG_CONTEXT, 'deleteProtocol success', { protocolId });

    return {
      success: true,
      toast: () =>
        showToast('Protocol deleted successfully!', 'success', 'top-right', 3000),
    };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      'deleteProtocol failed',
      error.response?.data ?? error
    );
    return resolveProtocolError(error);
  }
};

/**
 * Add products to a protocol's existing link set (idempotent).
 *
 * Unlike `product_ids` on PATCH, this **adds** rather than replaces.
 *
 * @async
 * @param {string} token - Access token for the current user.
 * @param {string|number} protocolId
 * @param {Array<number>} productIds
 * @returns {Promise<Object>} `{ success: true, protocol }` (full updated protocol payload)
 * or the structured failure.
 */
export const attachProductsToProtocol = async (
  token,
  protocolId,
  productIds
) => {
  try {
    logInfo(LOG_CONTEXT, 'attachProductsToProtocol start', {
      protocolId,
      productIds,
    });

    const response = await axios.post(
      `${BACKEND_URL}protocols/${protocolId}/products/`,
      { product_ids: productIds },
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    logInfo(LOG_CONTEXT, 'attachProductsToProtocol success', {
      protocolId,
      productCount: response.data?.product_count,
    });

    return { success: true, protocol: response.data };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      'attachProductsToProtocol failed',
      error.response?.data ?? error
    );
    return resolveProtocolError(error);
  }
};

/**
 * Remove products from a protocol (idempotent).
 *
 * Axios only sends a DELETE body when it is passed as `config.data`.
 *
 * @async
 * @param {string} token - Access token for the current user.
 * @param {string|number} protocolId
 * @param {Array<number>} productIds
 * @returns {Promise<Object>} `{ success: true, protocol }` or the structured failure.
 */
export const detachProductsFromProtocol = async (
  token,
  protocolId,
  productIds
) => {
  try {
    logInfo(LOG_CONTEXT, 'detachProductsFromProtocol start', {
      protocolId,
      productIds,
    });

    const response = await axios.delete(
      `${BACKEND_URL}protocols/${protocolId}/products/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
        data: { product_ids: productIds },
      }
    );

    logInfo(LOG_CONTEXT, 'detachProductsFromProtocol success', {
      protocolId,
      productCount: response.data?.product_count,
    });

    return { success: true, protocol: response.data };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      'detachProductsFromProtocol failed',
      error.response?.data ?? error
    );
    return resolveProtocolError(error);
  }
};

/**
 * Fetch the protocol stubs currently linked to a product.
 *
 * @async
 * @param {string} token - Access token for the current user.
 * @param {string|number} productId
 * @returns {Promise<Object>} `{ success: true, protocols }` where each stub is
 * `{ id, name, url }`, or the structured failure.
 */
export const getProductProtocols = async (token, productId) => {
  try {
    logInfo(LOG_CONTEXT, 'getProductProtocols start', { productId });

    const response = await axios.get(
      `${BACKEND_URL}products/${productId}/protocols/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    const protocols = Array.isArray(response.data) ? response.data : [];

    logInfo(LOG_CONTEXT, 'getProductProtocols success', {
      productId,
      count: protocols.length,
    });

    return { success: true, protocols };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      'getProductProtocols failed',
      error.response?.data ?? error
    );
    return resolveProtocolError(error);
  }
};

/**
 * Link protocols to a product (idempotent).
 *
 * Rejected with 400 for supplier catalog (Shop) products.
 *
 * @async
 * @param {string} token - Access token for the current user.
 * @param {string|number} productId
 * @param {Array<number>} protocolIds
 * @returns {Promise<Object>} `{ success: true, protocols }` (the product's current
 * stubs) or the structured failure.
 */
export const attachProtocolsToProduct = async (
  token,
  productId,
  protocolIds
) => {
  try {
    logInfo(LOG_CONTEXT, 'attachProtocolsToProduct start', {
      productId,
      protocolIds,
    });

    const response = await axios.post(
      `${BACKEND_URL}products/${productId}/protocols/`,
      { protocol_ids: protocolIds },
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    const protocols = Array.isArray(response.data) ? response.data : [];

    logInfo(LOG_CONTEXT, 'attachProtocolsToProduct success', {
      productId,
      count: protocols.length,
    });

    return { success: true, protocols };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      'attachProtocolsToProduct failed',
      error.response?.data ?? error
    );
    return resolveProtocolError(error);
  }
};

/**
 * Unlink protocols from a product (idempotent).
 *
 * Axios only sends a DELETE body when it is passed as `config.data`.
 *
 * @async
 * @param {string} token - Access token for the current user.
 * @param {string|number} productId
 * @param {Array<number>} protocolIds
 * @returns {Promise<Object>} `{ success: true, protocols }` or the structured failure.
 */
export const detachProtocolsFromProduct = async (
  token,
  productId,
  protocolIds
) => {
  try {
    logInfo(LOG_CONTEXT, 'detachProtocolsFromProduct start', {
      productId,
      protocolIds,
    });

    const response = await axios.delete(
      `${BACKEND_URL}products/${productId}/protocols/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
        data: { protocol_ids: protocolIds },
      }
    );

    const protocols = Array.isArray(response.data) ? response.data : [];

    logInfo(LOG_CONTEXT, 'detachProtocolsFromProduct success', {
      productId,
      count: protocols.length,
    });

    return { success: true, protocols };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      'detachProtocolsFromProduct failed',
      error.response?.data ?? error
    );
    return resolveProtocolError(error);
  }
};
